"use client";

import fireValuesProvided from "@/lib/tagular/fire-values-provided";
import generateTagularUUID from "@/lib/tagular/generate-tagular-uuid";
import type {
  TAGULAR_VALUES_PROVIDED_EVENT,
  TagularValuesProvidedReturnValue,
} from "@/types/tagular";
import {
  MonarchEvaluationsRuleSet,
  MonarchEvaluationsTrafficFlow,
  useMonarch,
} from "@redventures/cohesion-utils-react";
import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
// import { mockUseMonarchReturn } from "../../tests/mocks/mock-use-monarch";

interface GlobalSlotContextValue {
  getSlotId: (baseSlotId: string) => string;
}

interface GlobalSlotProviderProps {
  children: ReactNode;
}

type ExperienceNumber = number;
type DynamicSlotValueDecision = string;

interface DynamicSlotRule {
  value: DyanmicSlotRuleValue;
  returnType: string;
  returnDetails: {
    type: string;
    returnValueId: string;
    returnValueName: string;
  };
  isRuleActive: boolean;
  audienceId: string;
  audienceName: string;
}

interface DyanmicSlotRuleValue {
  [key: string]: DynamicSlotValueDecision;
}

interface GlobalSlotState {
  globalExpVariant?: string;
  allSlots?: string[];
}

interface ExperienceValue {
  slot: {
    slotId: string;
    slotName: string;
    customId: string;
  };
  value?: {
    componentId?: string;
    componentName?: string;
    componentCustomId?: string;
  };
}

interface DynamicSlotReturnDetails {
  type: "experience";
  experienceNumber: ExperienceNumber;
  experienceValues: ExperienceValue[];
}

const getGlobalRule = <T,>(
  rules: MonarchEvaluationsRuleSet,
  ruleName: string
): T | null => {
  return (rules[ruleName] as T | undefined) ?? null;
};

const isDynamicSlotReturnDetails = (
  returnDetails: unknown
): returnDetails is DynamicSlotReturnDetails => {
  if (!returnDetails || typeof returnDetails !== "object") {
    return false;
  }

  const value = returnDetails as Record<string, unknown>;

  return (
    value.type === "experience" && typeof value.experienceNumber === "number"
  );
};

const isTrafficFlowExperience = (
  trafficFlow: MonarchEvaluationsTrafficFlow | undefined
): trafficFlow is MonarchEvaluationsTrafficFlow & {
  returnType: "experience";
  value: {
    experienceId: string;
    experienceName: string;
    experienceNumber: number;
    experienceValues: ExperienceValue[];
  };
} => {
  return (
    trafficFlow?.returnType === "experience" &&
    typeof trafficFlow.value === "object" &&
    trafficFlow.value !== null &&
    Array.isArray(
      (trafficFlow.value as { experienceValues?: unknown }).experienceValues
    ) &&
    typeof (trafficFlow.value as { experienceNumber?: unknown })
      .experienceNumber === "number" &&
    typeof (trafficFlow.value as { experienceId?: unknown }).experienceId ===
      "string" &&
    typeof (trafficFlow.value as { experienceName?: unknown })
      .experienceName === "string"
  );
};

const GlobalSlotContext = createContext<GlobalSlotContextValue | null>(null);

const GlobalSlotProvider: FC<GlobalSlotProviderProps> = ({ children }) => {
  const monarch = useMonarch();
  // const monarch = mockUseMonarchReturn;
  const lastValuesProvidedRequestIdRef = useRef<string | null>(null);

  const globalSlotState = useMemo<GlobalSlotState>(() => {
    if (monarch.isLoading) {
      return {};
    }

    const trafficFlow = monarch.rules.trafficFlow;
    const returnDetails = trafficFlow?.returnDetails;
    const dynamicSlotReturnDetails = isDynamicSlotReturnDetails(returnDetails)
      ? returnDetails
      : null;

    // get global dynamic slots rule  - {1: "a", 2: "b"}
    const globlDynamicSlots = getGlobalRule<DynamicSlotRule>(
      monarch.rules,
      "dynamicSlots"
    );

    // get traffic flow experience number
    const experienceNumber = dynamicSlotReturnDetails?.experienceNumber ?? null;

    const allSlots =
      trafficFlow?.returnType === "experience"
        ? trafficFlow?.value.experienceValues.map(
            (value) => value.slot.customId
          )
        : [];

    return {
      // check if the DynamicSlot return value has a matching experience
      //   get value and send through context
      globalExpVariant:
        experienceNumber !== null &&
        globlDynamicSlots?.value?.[experienceNumber]
          ? globlDynamicSlots.value[experienceNumber]
          : "",
      // get all the slots so we can compare later
      allSlots,
    };
  }, [monarch.isLoading, monarch.rules]);

  // this checks if the provided slot is part of the split test
  const getSlotId = useCallback(
    (baseSlotId: string) => {
      // generate the new slotId if a global experiment variant is active
      const generatedSlotId = globalSlotState.globalExpVariant
        ? `${baseSlotId}${globalSlotState.globalExpVariant}`
        : null;

      // check if generated slot is in the activeSlots to ensure the new slot will be filled
      if (
        generatedSlotId &&
        globalSlotState.allSlots?.includes(generatedSlotId)
      ) {
        // return the new dynamic name
        return generatedSlotId;
      }

      // otherwise return the old name
      return baseSlotId;
    },
    [globalSlotState]
  );

  const value = useMemo(() => {
    return {
      getSlotId,
    };
  }, [getSlotId]);

  useEffect(() => {
    if (monarch.isLoading) {
      return;
    }

    const trafficFlow = monarch.rules.trafficFlow;
    const dynamicSlotsRule = getGlobalRule<DynamicSlotRule>(
      monarch.rules,
      "dynamicSlots"
    );

    if (
      !trafficFlow ||
      !dynamicSlotsRule ||
      !isTrafficFlowExperience(trafficFlow)
    ) {
      return;
    }

    const experienceNumber = trafficFlow.value.experienceNumber;
    const matchedDecision = dynamicSlotsRule.value?.[experienceNumber];

    if (!matchedDecision) {
      return;
    }

    const returnDetails = dynamicSlotsRule.returnDetails;
    const slots = trafficFlow.value.experienceValues.map((experienceValue) => ({
      slotId: experienceValue.slot.slotId,
      slotName: experienceValue.slot.slotName,
      returnableId: experienceValue.value?.componentId ?? "",
      returnableName: experienceValue.value?.componentName ?? "",
    }));

    const returnValue: TagularValuesProvidedReturnValue = {
      returnValueId: returnDetails.returnValueId,
      returnValueName: returnDetails.returnValueName,
      rule: {
        ruleId: dynamicSlotsRule.audienceId,
        ruleName: dynamicSlotsRule.audienceName,
        customId: "dynamicSlots",
      },
      returnValueMetadata: {
        decisionResult: JSON.stringify(dynamicSlotsRule.value),
      },
      experience: {
        experienceNumber: experienceNumber ? String(experienceNumber) : "",
        experienceId: trafficFlow.value.experienceId,
        experienceName: trafficFlow.value.experienceName,
        slots,
      },
    };

    const requestId = generateTagularUUID();

    if (lastValuesProvidedRequestIdRef.current === requestId) {
      return;
    }

    const payloadProperties = Object.entries(dynamicSlotsRule.value).map(
      ([key, value]) => ({
        key: String(key),
        value: String(value),
        valueType: "string",
      })
    );

    const payload: TAGULAR_VALUES_PROVIDED_EVENT = {
      requestId,
      returnValues: [returnValue],
      payload: payloadProperties,
    };

    fireValuesProvided(payload);
    lastValuesProvidedRequestIdRef.current = requestId;
  }, [monarch.isLoading, monarch.rules]);

  return (
    <GlobalSlotContext.Provider value={value}>
      {children}
    </GlobalSlotContext.Provider>
  );
};

const useGlobalSlotContext = () => {
  return useContext(GlobalSlotContext);
};

export { GlobalSlotProvider, useGlobalSlotContext };
